import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import useProgressStore from '../stores/useProgressStore';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import Hls from 'hls.js';

const LessonPage = () => {
  const { user } = useAuthStore();
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { course, fetchCourse, isLoading: courseLoading, error: courseError } = useCourseStore();
  const { progress, fetchProgress, markLessonComplete: markLessonCompleteApi, isLoading: progressLoading } = useProgressStore();
  const toast = useToast();
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(-1);
  const [quizScore, setQuizScore] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMarkAsComplete, setIsLoadingMarkAsComplete] = useState(false);
  const [resolution, setResolution] = useState(() => {
    return localStorage.getItem('preferredResolution') || 'auto';
  });
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const REFRESH_INTERVAL = 45 * 1000;
  const refreshTimerRef = useRef(null);
  const [resourceUrl, setResourceUrl] = useState(null);
  const [isLoadingResource, setIsLoadingResource] = useState(false);
  const resourceRefreshTimer = useRef(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (
      <Toolbar>
        {(slots) => {
          const { ZoomIn, ZoomOut, SwitchTheme } = slots;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ZoomOut />
              <ZoomIn />
              <SwitchTheme />
            </div>
          );
        }}
      </Toolbar>
    ),
  });

  useEffect(() => {
    fetchCourse(courseId);
    fetchProgress(courseId);
  }, [courseId]);

  useEffect(() => {
    if (!course || !lessonId) return;
    let foundLesson = null;
    let foundModuleIndex = -1;
    for (let i = 0; i < (course.modules?.length || 0); i++) {
      const module = course.modules[i];
      const lesson = module.lessons?.find(l => l._id?.toString() === lessonId.toString());
      if (lesson) {
        foundLesson = lesson;
        foundModuleIndex = i;
        break;
      }
    }
    setCurrentLesson(foundLesson);
    setCurrentModuleIndex(foundModuleIndex);
    if (foundLesson?.quiz) {
      const scoreEntry = progress?.quizScores?.find(
        q => (q.quiz?._id || q.quiz || q._id)?.toString() === foundLesson.quiz._id.toString()
      );
      if (scoreEntry) setQuizScore(scoreEntry);
    }
  }, [course, lessonId, progress]);

  useEffect(() => {
    if (currentLesson?.resources?.[0]) {
      if (resourceRefreshTimer.current) clearInterval(resourceRefreshTimer.current);
      fetchResourceUrl();
      resourceRefreshTimer.current = setInterval(fetchResourceUrl, REFRESH_INTERVAL);
    }
    return () => {
      if (resourceRefreshTimer.current) clearInterval(resourceRefreshTimer.current);
    };
  }, [currentLesson?.resources]);

  const fetchResourceUrl = async () => {
    try {
      const resourceId = currentLesson?.resources?.[0]?._id || currentLesson?.resources?.[0];
      if (!resourceId) return;
      setIsLoadingResource(true);
      const res = await axiosClient.get(`/resources/${resourceId}/student/signed-url`);
      const newUrl = res.data?.data?.signedUrl;
      setResourceUrl(newUrl);
    } catch (err) {
      console.error('Error fetching resource signed URL:', err);
    } finally {
      setIsLoadingResource(false);
    }
  };

  useEffect(() => {
    if (currentLesson?.video) {
      getSignedUrl();
      refreshTimerRef.current = setInterval(() => getSignedUrl(true), REFRESH_INTERVAL);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [currentLesson?.video, resolution]);

  const getSignedUrl = async (isRefresh = false) => {
    try {
      const videoId = currentLesson?.video?._id || currentLesson?.video;
      if (!videoId) return;
      setIsLoading(true);
      const res = await axiosClient.get(`/videos/learn/${videoId}/signed-url/${resolution}`);
      const newUrl = res.data?.data?.originalUrl;
      if (!newUrl) throw new Error('Original URL not found');
      if (isRefresh && videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        const isPlaying = !videoRef.current.paused;
        attachVideoSource(newUrl, isRefresh, currentTime, isPlaying);
      } else {
        attachVideoSource(newUrl);
      }
    } catch (err) {
      console.error(`Error fetching ${resolution} URL:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const attachVideoSource = (url, isRefresh = false, currentTime = 0, isPlaying = false) => {
    const video = videoRef.current;
    if (!video) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setVideoUrl(url);

    if (Hls.isSupported() && url.includes('.m3u8')) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) hls.destroy();
      });
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl') && url.includes('.m3u8')) {
      video.src = url;
    } else {
      video.src = url;
    }

    if (isRefresh) {
      video.currentTime = currentTime;
      if (isPlaying) video.play().catch(() => { });
    }
  };

  const handleResolutionChange = (newRes) => {
    setResolution(newRes);
    localStorage.setItem('preferredResolution', newRes);
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const position = videoRef.current.currentTime;
      setPlaybackPosition(position);
    }
  };

  const handleVideoEnded = () => {
    handleMarkComplete();
  };

  const handleMarkComplete = async () => {
    try {
      setIsLoadingMarkAsComplete(true)
      await markLessonCompleteApi(currentLesson._id, courseId);
      setIsLoadingMarkAsComplete(false)
      toast.success('Lesson marked as complete 🎉');
      fetchProgress(courseId);
    } catch (error) {
      toast.error('Failed to mark lesson complete');
      console.error(error);
    } finally {
      setIsLoadingMarkAsComplete(false)
    }
  };

  const handleTakeQuiz = () => {
    navigate(`/student/quiz/${courseId}/${currentLesson.quiz._id}`);
  };

  const handleViewAssignment = () => {
    navigate(`/student/${courseId}/assignment/${currentLesson.assignment._id}`);
  };

  const toggleModule = (index) => {
    setExpandedModules((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getModuleProgress = (moduleLessons) => {
    if (!progress?.completedLessons || !moduleLessons) return 0;
    const completed = moduleLessons.filter((lesson) =>
      progress.completedLessons.includes(lesson._id.toString())
    ).length;
    return moduleLessons.length ? Math.round((completed / moduleLessons.length) * 100) : 0;
  };

  const getModuleCompletion = (moduleLessons) => {
    if (!progress?.completedLessons || !moduleLessons) return 0;
    const completed = moduleLessons.filter((lesson) =>
      progress.completedLessons.includes(lesson._id.toString())
    ).length;
    return moduleLessons.length ? Math.round((completed / moduleLessons.length) * 100) : 0;
  };

  if ((courseLoading && !course) || (progressLoading && !progress)) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (courseError || !course || !currentLesson) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Lesson not found</h2>
            <button
              onClick={() => navigate(`/student/learning/${courseId}`)}
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Go to course curriculum
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Lesson Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/student/learning/${courseId}`)}
              className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to course
            </button>
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
              {currentLesson.title}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              {course.title}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">

              {currentLesson?.video && (
                <div className="relative bg-black rounded-lg overflow-hidden mb-6">
                  <div className="absolute top-3 left-3 z-20 flex gap-2">
                    {['auto', '240p', '360p', '480p', '720p', '1080p'].map((res) => (
                      <button
                        key={res}
                        onClick={() => handleResolutionChange(res)}
                        className={`px-2 py-1 text-xs sm:text-sm font-medium rounded ${resolution === res
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                      >
                        {res}
                      </button>
                    ))}
                  </div>
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
                      <div className="w-10 h-10 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
                    </div>
                  )}
                  <video
                    ref={videoRef}
                    controls
                    controlsList="nodownload"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full max-h-[80vh] rounded-lg select-none pointer-events-auto"
                    onTimeUpdate={handleVideoProgress}
                    onEnded={handleVideoEnded}
                    onLoadedData={() => setIsLoading(false)}
                  />
                </div>
              )}

              {currentLesson?.textDescription && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
                  <div
                    className="prose dark:prose-invert max-w-none [&_img]:max-w-full [&_img]:rounded-lg"
                    dangerouslySetInnerHTML={{
                      __html: currentLesson.textDescription,
                    }}
                  />
                </div>
              )}

              {currentLesson?.resources?.[0] && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
                  <div className="mb-4">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {currentLesson.resources[0].originalName}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                    <div>
                      <p className="text-neutral-500 dark:text-neutral-400">File Size</p>
                      <p className="font-medium dark:text-neutral-300">
                        {(currentLesson.resources[0].fileSize / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <div>
                      <p className="text-neutral-500 dark:text-neutral-400">File Type</p>
                      <p className="font-medium dark:text-neutral-300">
                        {currentLesson.resources[0].fileType || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-black rounded-lg flex justify-center items-center overflow-hidden">
                    {isLoadingResource ? (
                      <div className="w-full h-[70vh] flex justify-center items-center">
                        <svg
                          className="animate-spin h-8 w-8 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                      </div>
                    ) : !resourceUrl ? (
                      <p className="text-gray-400 p-6 text-center dark:text-neutral-300">
                        No secure resource available.
                      </p>
                    ) : currentLesson.resources[0].fileType?.includes('image') ? (
                      <img
                        src={resourceUrl}
                        alt={currentLesson.resources[0].title || 'Preview'}
                        className="max-h-[80vh] object-contain rounded-lg"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                      />
                    ) : currentLesson.resources[0].fileType?.includes('pdf') ? (
                      resourceUrl ? (
                        <div className="w-full h-[80vh] bg-white rounded-lg">
                          <Worker workerUrl="/pdf.worker.min.js">
                            <Viewer
                              fileUrl={resourceUrl}
                              plugins={[defaultLayoutPluginInstance]}
                              onError={(error) => {
                                console.error('PDF Viewer Error:', error);
                                toast.error('Failed to load PDF. Please try again.');
                              }}
                            />
                          </Worker>
                        </div>
                      ) : isLoadingResource ? (
                        <div className="w-full h-[70vh] flex justify-center items-center">
                          <svg className="animate-spin h-8 w-8 text-white" />
                        </div>
                      ) : (
                        <p className="text-gray-400 p-6 text-center dark:text-neutral-300">
                          Failed to load document.
                        </p>)
                    ) : (
                      <p className="text-gray-400 text-center p-6 dark:text-neutral-300">
                        Preview not available for this file type.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {currentLesson.assignment && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 ">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">Assignment</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white">{currentLesson?.assignment?.title}</h4>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{currentLesson?.assignment?.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Due Date</p>
                        <p className="font-medium dark:text-neutral-300">
                          {currentLesson?.assignment?.dueDate ? new Date(currentLesson?.assignment?.dueDate).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Points</p>
                        <p className="font-medium dark:text-neutral-300">{currentLesson?.assignment?.points}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Late Submissions</p>
                        <p className="font-medium dark:text-neutral-300">
                          {currentLesson?.assignment?.allowLateSubmissions ? 'Allowed' : 'Not Allowed'}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Submission Types</p>
                        <p className="font-medium dark:text-neutral-300">{currentLesson?.assignment?.submissionTypes?.join(', ')}</p>
                      </div>
                    </div>
                    {currentLesson?.assignment?.instructions && (
                      <div>
                        <h4 className="font-medium text-neutral-900 dark:text-white mb-2">Instructions</h4>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {currentLesson?.assignment?.instructions}
                        </p>
                      </div>
                    )}
                    <button
                      onClick={handleViewAssignment}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      View Assignment
                    </button>
                  </div>
                </div>
              )}

              {currentLesson.quiz && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 ">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">Quiz</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white">{currentLesson?.quiz?.title}</h4>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{currentLesson?.quiz?.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Time Limit</p>
                        <p className="font-medium dark:text-neutral-400">{currentLesson?.quiz?.timeLimit} minutes</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Passing Score</p>
                        <p className="font-medium dark:text-neutral-400">{currentLesson?.quiz?.passingScore}%</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Questions</p>
                        <p className="font-medium dark:text-neutral-400">{currentLesson?.quiz?.questions?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500 dark:text-neutral-400">Attempts</p>
                        <p className="font-medium dark:text-neutral-400">{currentLesson?.quiz?.attemptsAllowed}</p>
                      </div>
                    </div>
                  </div>
                  {!quizScore && (
                    <button
                      onClick={handleTakeQuiz}
                      className="px-4 py-2 mt-8 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      Take Quiz
                    </button>
                  )}
                </div>
              )}

              {currentLesson.quiz && quizScore && (
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 ">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Your Quiz Results</h3>
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${quizScore.passed
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                      {quizScore.passed ? 'Passed' : 'Failed'}
                    </div>
                    <div className="text-neutral-700 dark:text-neutral-300">
                      Score: {quizScore.score} / {currentLesson.quiz.totalPoints} ({quizScore.percentage.toFixed(1)}%)
                    </div>
                    <div className="text-neutral-500 dark:text-neutral-400 text-sm">
                      Attempts: {quizScore.attempts}
                    </div>
                  </div>
                  {quizScore.attempts < currentLesson.quiz.attemptsAllowed && (
                    <button
                      onClick={() => navigate(`/student/quiz/${courseId}/${currentLesson.quiz._id}`)}
                      className="mt-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded-lg"
                    >
                      Retake Quiz ({currentLesson.quiz.attemptsAllowed - quizScore.attempts} attempts left)
                    </button>
                  )}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-4">
                <button
                  onClick={handleMarkComplete}
                  disabled={isLoadingMarkAsComplete}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none  ${isLoadingMarkAsComplete ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600 focus:ring-2 focus:ring-green-500'}`}
                >
                  {isLoadingMarkAsComplete ? 'Please wait...' : 'Mark as Complete'}
                </button>
              </div>
            </div>

            {/* Curriculum Sidebar — unchanged */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 h-fit">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Course Curriculum</h2>
              <div className="space-y-4">
                {course.modules?.map((module, moduleIndex) => {
                  const moduleCompletion = getModuleCompletion(module.lessons);
                  const moduleProgress = getModuleProgress(module.lessons);
                  const isCurrentModule = moduleIndex === currentModuleIndex;
                  const isModuleCompleted = moduleCompletion === 100;
                  const isExpanded = expandedModules[moduleIndex] ?? true;
                  return (
                    <div
                      key={module._id || moduleIndex}
                      className={`border rounded-lg ${isCurrentModule
                        ? "border-primary-500 bg-primary-50 dark:bg-primary-900/10"
                        : "border-neutral-200 dark:border-neutral-700"
                        }`}
                    >
                      <div
                        className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-t-lg flex flex-wrap justify-between items-center cursor-pointer gap-y-2"
                        onClick={() => toggleModule(moduleIndex)}
                      >
                        <div className="flex flex-wrap items-center gap-x-2 min-w-0">
                          <div className="truncate">
                            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mr-2">
                              Module {moduleIndex + 1}
                            </span>
                            <span className="font-medium text-neutral-900 dark:text-white break-words">
                              {module.title}
                            </span>
                          </div>
                          <span className="ml-1 px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded shrink-0">
                            {moduleProgress}%
                          </span>
                          {isModuleCompleted && (
                            <span className="text-xs text-green-600 dark:text-green-400 shrink-0">✓</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-neutral-500 dark:text-neutral-400 shrink-0">
                          <span className="whitespace-nowrap">{module.lessons?.length || 0} lessons</span>
                          <span>{isExpanded ? "▲" : "▼"}</span>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="p-3 space-y-1">
                          {module.lessons?.map((lesson, lessonIndex) => {
                            const isCurrentLesson = lesson._id?.toString() === lessonId;
                            const isCompleted = progress?.completedLessons?.includes(lesson._id?.toString());
                            return (
                              <div
                                key={lesson._id || lessonIndex}
                                className={`flex items-center justify-between p-2 rounded cursor-pointer text-sm ${isCurrentLesson
                                  ? "bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium"
                                  : "hover:bg-neutral-100 dark:hover:bg-neutral-700/50"
                                  }`}
                                onClick={() =>
                                  navigate(`/student/learning/${courseId}/lesson/${lesson._id}`)
                                }
                              >
                                <div className="flex items-center dark:text-neutral-400">
                                  <span className="text-neutral-500 dark:text-neutral-400 mr-2">
                                    {moduleIndex + 1}.{lessonIndex + 1}
                                  </span>
                                  <span>{lesson.title}</span>
                                </div>
                                {isCompleted && (
                                  <span className="text-xs text-green-600 dark:text-green-400">✓</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LessonPage;